import React, { ReactElement } from 'react'
import { muiBankTheme, ScreenReaderOnly, TextBody } from '@ally/metronome-ui'
import logRocket from 'logrocket'
import { asCurrency } from '@ally/aos-utils'
import {
  Account,
  AccountStatus,
  AccountType,
} from '@ally/data-types-afg-accounts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { AllyUserRole, HostServices } from '@ally/federated-types'
import toArray from 'dayjs/plugin/toArray'

import { SERVER_TIMEZONE } from './constants'
import {
  AdobeResponse,
  AdobeTile,
  MboxContent,
  MboxResponseObj,
  GroupedAccounts,
  StkSummary,
  CreditCardNames,
} from './types'
import { TextBodyOverride } from './components/Shared/MetronomeOverrides'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(toArray)

export const noop = ((): void => {
  /* noop */
}) as any

export enum AccountTypeLabel {
  INTEREST_CHECKING = 'INTEREST CHECKING',
  SAVINGS = 'SAVINGS',
  CDs = 'CDs',
  INVESTMENTS = 'INVESTMENTS',
  MORTGAGE = 'MORTGAGE',
  HOME_EQUITY = 'HOME_EQUITY',
  VEHICLE = 'VEHICLE',
  TRUST = 'TRUST',
  MARGIN = 'MARGIN',
  CC = 'CC',
}

export const AccountTypeToLabel: Record<AccountType, AccountTypeLabel> = {
  [AccountType.DDA]: AccountTypeLabel.INTEREST_CHECKING,
  [AccountType.MMA]: AccountTypeLabel.SAVINGS,
  [AccountType.SDA]: AccountTypeLabel.SAVINGS,
  [AccountType.CMG]: AccountTypeLabel.SAVINGS,
  [AccountType.CDA]: AccountTypeLabel.CDs,
  [AccountType.INVS]: AccountTypeLabel.INVESTMENTS,
  [AccountType.INVA]: AccountTypeLabel.INVESTMENTS,
  [AccountType.INVW]: AccountTypeLabel.INVESTMENTS,
  [AccountType.MORTGAGE]: AccountTypeLabel.MORTGAGE,
  [AccountType.HOME_EQUITY]: AccountTypeLabel.HOME_EQUITY,
  [AccountType.LOAN]: AccountTypeLabel.VEHICLE,
  [AccountType.OPERATING_LEASE]: AccountTypeLabel.VEHICLE,
  [AccountType.FINANCE_LEASE]: AccountTypeLabel.VEHICLE,
  [AccountType.HIRE_PURCHASE]: AccountTypeLabel.VEHICLE,
  [AccountType.MARGIN]: AccountTypeLabel.MARGIN,
  [AccountType.TRUST]: AccountTypeLabel.TRUST,
  [AccountType.PLATINUM]: AccountTypeLabel.CC,
  [AccountType.EVERYDAY]: AccountTypeLabel.CC,
  [AccountType.UNLIMITED]: AccountTypeLabel.CC,
}

export enum ValueHeading {
  ACCOUNT_VALUE = 'Account Value',
  AVAILABLE = 'Available',
  CURRENT = 'Current',
  CURRENT_BALANCE = 'Current Balance',
  CLOSED = 'Closed',
  PAYMENT_DUE = 'Payment Due',
}

export type LabelHeadingKeys = Extract<
  AccountTypeLabel,
  | AccountTypeLabel.INVESTMENTS
  | AccountTypeLabel.INTEREST_CHECKING
  | AccountTypeLabel.SAVINGS
  | AccountTypeLabel.CDs
  | AccountTypeLabel.MORTGAGE
  | AccountTypeLabel.HOME_EQUITY
  | AccountTypeLabel.VEHICLE
  | AccountTypeLabel.CC
>

export const labelHeadings: Record<LabelHeadingKeys, ValueHeading> = {
  [AccountTypeLabel.INVESTMENTS]: ValueHeading.ACCOUNT_VALUE,
  [AccountTypeLabel.INTEREST_CHECKING]: ValueHeading.AVAILABLE,
  [AccountTypeLabel.SAVINGS]: ValueHeading.AVAILABLE,
  [AccountTypeLabel.CDs]: ValueHeading.CURRENT,
  [AccountTypeLabel.MORTGAGE]: ValueHeading.PAYMENT_DUE,
  [AccountTypeLabel.HOME_EQUITY]: ValueHeading.PAYMENT_DUE,
  [AccountTypeLabel.VEHICLE]: ValueHeading.PAYMENT_DUE,
  [AccountTypeLabel.CC]: ValueHeading.CURRENT_BALANCE,
}

export interface AccountTypeSortWeight {
  [key: string]: number
}

export const investSortWeightLookupObj: AccountTypeSortWeight = {
  INVW: 3,
  INVA: 2,
  INVS: 1,
}

export const bankSortWeightLookupObj: AccountTypeSortWeight = {
  CHECKING: 4,
  SAVINGS: 3,
  CD: 2,
  IRA: 1,
}

interface GetBalanceProps {
  accountType: string
  availableBalance: number
  currentBalance: number
  paymentDue?: number
}

export const getBalance = ({
  accountType,
  availableBalance,
  currentBalance,
  paymentDue,
}: GetBalanceProps): number => {
  if (
    accountType === AccountType.INVW ||
    accountType === AccountType.INVA ||
    accountType === AccountType.INVS ||
    accountType === AccountType.CDA ||
    accountType === AccountType.PLATINUM ||
    accountType === AccountType.EVERYDAY ||
    accountType === AccountType.UNLIMITED
  ) {
    return currentBalance
  }
  if (
    accountType === AccountType.LOAN ||
    accountType === AccountType.OPERATING_LEASE ||
    accountType === AccountType.FINANCE_LEASE ||
    accountType === AccountType.HIRE_PURCHASE
  ) {
    return paymentDue || 0
  }

  return availableBalance
}

export function formatBalance(balance: number): ReactElement | string {
  if (balance < 0) {
    return (
      <TextBodyOverride tag="span" size="sm" color="error">
        {asCurrency(balance)}
      </TextBodyOverride>
    )
  }
  return asCurrency(balance)
}

// this is to address the test only issue when a long negative decimal amount is returned by the API - for example -0.00052 was returning as 0$.00.
export function formatNoNegativeBalance(
  balance: number,
): ReactElement | string {
  if (balance < 0) {
    return (
      <TextBody tag="span" size="sm">
        $0.00
      </TextBody>
    )
  }
  return asCurrency(balance)
}

export function formatClosedDate(dateStr: string): ReactElement {
  const closedDate = new Date(dateStr)
  return (
    <TextBodyOverride tag="span" size="sm" color="error">
      Closed {dayjs(closedDate).format('MMM DD, YYYY')}
    </TextBodyOverride>
  )
}

export function formatAsServerDate(
  dateString: string,
  formatString: string,
): string {
  return dayjs(dateString).tz(SERVER_TIMEZONE).format(formatString)
}

export function formatMaturityDate(dateStr: string): ReactElement {
  return (
    <TextBody tag="span" size="sm">
      {formatAsServerDate(dateStr, 'MMM DD, YYYY')}
    </TextBody>
  )
}

export const filterOpenDepositAccounts = (accounts: Account[]): Account[] => {
  return accounts.filter(account => account.status !== AccountStatus.CLOSED)
}

/**
 * Filter accounts closed more than 60 days.
 */
export const filterActiveDepositAccounts = (accounts: Account[]): Account[] => {
  return accounts.filter(({ closeDate }) => {
    if (!closeDate) {
      return true
    }
    const today = new Date()
    const dayDifference = today.getDate() - 60
    const unixSixtyDaysAgo = today.setDate(dayDifference)
    const sixtyDaysAgo = new Date(unixSixtyDaysAgo).getTime()
    const isMoreThan60 = new Date(closeDate).getTime() < sixtyDaysAgo
    return !isMoreThan60
  })
}

export const formatPercentage = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return ''
  }
  return `${value.toFixed(2)}%`
}

export function dasherize(string: string): string {
  return string
    .replace(/[A-Z]/g, function (char, index) {
      return (index !== 0 ? '-' : '') + char.toLowerCase()
    })
    .replace(/\s/g, '-')
}

export const accountGroup = (
  accountType: string,
  retirementIndicator: boolean,
): string => {
  if (retirementIndicator) {
    return 'IRA'
  }

  switch (accountType) {
    case 'MMA':
    case 'CMG':
    case 'SDA':
      return 'SAVINGS'
    case 'CDA':
      return 'CD'
    case 'DDA':
      return 'CHECKING'
    case 'INVW':
      return 'INVW'
    case 'INVA':
      return 'INVA'
    case 'INVS':
      return 'INVS'
    default:
      return accountType
  }
}

export const sortAccountGroup = (
  lob: string,
  group: GroupedAccounts,
): GroupedAccounts => {
  const weightLookupObj =
    lob === 'INVEST' ? investSortWeightLookupObj : bankSortWeightLookupObj

  return Object.keys(group)
    .sort((a, b) => {
      const weightA = weightLookupObj[a]
      const weightB = weightLookupObj[b]
      // eslint-disable-next-line no-nested-ternary
      return weightA > weightB ? -1 : weightA < weightB ? 1 : 0
    })
    .reduce((result: GroupedAccounts, key: string) => {
      result[key] = group[key]
      return result
    }, {})
}

export function groupBankAccounts(accounts: Account[]): GroupedAccounts {
  if (accounts.length <= 0) {
    return {}
  }

  const reducedAccounts = accounts.reduce(
    (result: GroupedAccounts, account) => {
      const group: string = accountGroup(
        account.type,
        !!account.indicator?.retirement,
      )

      const balance =
        getBalance({
          accountType: account.type,
          availableBalance: account.balance?.available ?? 0,
          currentBalance: account.balance?.current ?? 0,
        }) || 0

      const {
        aggBalance = 0,
        numberOfAccounts = 0,
        aggInterestYtd = 0,
        aggInterestAccrued = 0,
        hasSingleIraType = false,
      } = result[group] || {}

      const interestAccrued = account.interest?.totalAccrued
        ? account.interest.totalAccrued + aggInterestAccrued
        : aggInterestAccrued

      return {
        ...result,
        [group]: {
          accountGroup: group,
          numberOfAccounts: numberOfAccounts + 1,
          aggBalance: aggBalance + balance,
          aggInterestYtd:
            (account.interest?.ytd ?? 0) + (aggInterestYtd as number),
          aggInterestAccrued: interestAccrued,
          hasSingleIraType,
        },
      }
    },
    {},
  )

  const iraAccountTypes = accounts.filter(
    account => account.indicator?.retirement === true,
  )
  const hasSingleIraType = iraAccountTypes.every(
    account => account.type === iraAccountTypes[0].type,
  )

  if (hasSingleIraType && 'IRA' in reducedAccounts) {
    return {
      ...reducedAccounts,
      IRA: { ...reducedAccounts.IRA, hasSingleIraType },
    }
  }

  return sortAccountGroup('DEPOSIT', reducedAccounts)
}

export function groupInvestAccounts(accounts: Account[]): GroupedAccounts {
  if (accounts.length <= 0) {
    return {}
  }

  const reducedAccounts = accounts.reduce(
    (result: GroupedAccounts, account) => {
      const group: string = accountGroup(account.type, false)
      const balance = getBalance({
        accountType: account.type,
        availableBalance: account.value?.cash ?? 0,
        currentBalance: account.value?.total ?? 0,
      })

      const {
        aggBalance = 0,
        numberOfAccounts = 0,
        aggAccountValueTotal = 0,
        aggCashMoneyMarketTotal = 0,
        aggMarketValueTotal = 0,
      } = result[group] || {}

      return {
        ...result,
        [group]: {
          accountGroup: group,
          numberOfAccounts: numberOfAccounts + 1,
          aggBalance: aggBalance + balance,
          aggAccountValueTotal:
            aggAccountValueTotal + (account.value?.total ?? 0),
          aggCashMoneyMarketTotal:
            aggCashMoneyMarketTotal + (account.value?.cash ?? 0),
          aggMarketValueTotal:
            aggMarketValueTotal + (account.value?.market ?? 0),
        },
      }
    },
    {},
  )

  return sortAccountGroup('INVEST', reducedAccounts)
}

/**
 * Sorts account based on account opening date,
 * and then groups them based on accountClosingDate.
 * Returns {activeAccounts[], closedAccounts[]}
 */
interface AccountList {
  activeAccounts: Account[]
  closedAccounts: Account[]
}

export const sortDepositAccountsGroup = (accounts: Account[]): AccountList => {
  const sortedAccounts = accounts.sort((accountA: Account, accountB: Account) =>
    accountA.openDate && accountB.openDate
      ? new Date(accountA.openDate).getTime() -
        new Date(accountB.openDate).getTime()
      : // if both are undefined then don't sort
        0,
  )

  return sortedAccounts.reduce(
    (accumulator, account) => {
      return account.closeDate
        ? {
            ...accumulator,
            closedAccounts: [...accumulator.closedAccounts, account],
          }
        : {
            ...accumulator,
            activeAccounts: [...accumulator.activeAccounts, account],
          }
    },
    { activeAccounts: [], closedAccounts: [] } as AccountList,
  )
}

/**
 * Sorts invest accounts based on accountStatus ensuring
 * Active statuses come before Closed statuses
 */
export const sortInvestAccountsGroup = (accounts: Account[]): Account[] => {
  return accounts.sort((accountA, accountB) => {
    const statusA = accountA.status === 'CLOSED' ? 1 : 0
    const statusB = accountB.status === 'CLOSED' ? 1 : 0
    if (statusA > statusB) return 1
    if (statusA < statusB) return -1
    return 0
  })
}

// Mbox
export function setLinkTarget(newTab: boolean): string {
  return newTab ? '_blank' : '_self'
}

export function verifyContent(
  mboxContent: AdobeResponse<MboxContent>,
): boolean {
  if (!Array.isArray(mboxContent)) {
    logRocket.captureMessage('Adobe response invalid format', {
      tags: {
        page: 'dashboard',
      },
      extra: {
        response: mboxContent,
      },
    })
    return false
  }
  const mboxSetJson = mboxContent.filter(
    item => item.action === 'setJson',
  )[0] as MboxResponseObj<MboxContent>
  const {
    ctaHref,
    ctaLabel,
    ctaNewTab,
    description,
    iconName,
    offerType,
  } = mboxSetJson.content[0]

  const hasHref = ctaHref !== undefined
  const hasLabel = ctaLabel !== undefined
  const hasNewTab = ctaNewTab !== undefined
  const hasDescription = description !== undefined
  const hasIcon = iconName !== undefined
  const hasOfferType = offerType !== undefined

  return (
    hasHref &&
    hasLabel &&
    hasNewTab &&
    hasDescription &&
    hasIcon &&
    hasOfferType
  )
}

export function validateTiles(
  tiles: AdobeTile[],
  requiredKeys: string[],
): boolean {
  return tiles.every(tile => {
    return requiredKeys.every(key => {
      const objKey = key as keyof AdobeTile
      return tile[objKey] !== undefined && tile[objKey] !== ''
    })
  })
}

export function validateObjKeys(obj: any, requiredKeys: string[]): boolean {
  const hasKeys = Object.keys(obj).length !== 0
  return (
    hasKeys &&
    requiredKeys.every(key => {
      return obj[key] !== undefined && obj[key] !== ''
    })
  )
}

export const iconBgColor = (lobName: string): string => {
  switch (lobName) {
    case 'invest':
      return muiBankTheme.colors.inv
    case 'mortgage':
      return muiBankTheme.colors.mortgage
    case 'bank':
    default:
      return muiBankTheme.colors.dda
  }
}

function bucketCount(account: Account): number {
  return account.meta?.stkSummary?.find(s => s.type === 'BUCKET')?.count ?? 0
}
export const boosterTypes = ['RUP', 'FAS', 'SIFI']

function boosterCount(account: Account): number {
  return (
    account.meta?.stkSummary
      ?.filter(s => boosterTypes.includes(s.type))
      .reduce((total, summary) => total + summary.count, 0) ?? 0
  )
}

export function getStkSummary(accounts: Account[]): StkSummary {
  return {
    // Count must include core savings + 1 additional bucket
    hasBuckets: accounts.some(acc => bucketCount(acc) > 1),
    hasBoosters: accounts.some(acc => {
      return boosterCount(acc) > 0
    }),
  }
}

export function getStkAccounts(accounts: Account[]): Account[] {
  return accounts.filter(acc => bucketCount(acc) > 1 || boosterCount(acc) > 0)
}

export function hasStaleAccounts(accounts: Account[]): boolean {
  return accounts.some(acct => !!acct.lastUpdated)
}

export function isAccountBlocked(account: Account): boolean {
  return !!account.indicator?.blocked
}

export function isMortgageAccountPaidInFull(account: Account): boolean {
  return (
    account.status === AccountStatus.PAIDOFF &&
    account.meta?.statusReason === 'PAID_IN_FULL'
  )
}

export function isMortgageAccountTransferred(account: Account): boolean {
  return (
    account.status === AccountStatus.PAIDOFF &&
    account.meta?.statusReason === 'TRANSFERRED'
  )
}

export function isMortgageAccountForeclosed(account: Account): boolean {
  return (
    account.status === AccountStatus.PAIDOFF &&
    account.meta?.statusReason === 'FORECLOSED'
  )
}

export function isMortgageAccountClaimsCloseOut(account: Account): boolean {
  return (
    account.status === AccountStatus.PAIDOFF &&
    account.meta?.statusReason === 'CLAIMS_CLOSE_OUT'
  )
}

/**
 * Represents the paid off mortgage status reasons that are handled in the UI for the
 * dash dash treatment or the Get Details PAIDOFF modal. Some PAIDOFF statusReasons
 * are shown as normal accounts with no special UI treatment.
 */
export function isMortgagePaidOffHandled(account: Account): boolean {
  return (
    isMortgageAccountPaidInFull(account) ||
    isMortgageAccountTransferred(account) ||
    isMortgageAccountForeclosed(account) ||
    isMortgageAccountClaimsCloseOut(account)
  )
}

export function isRestrictedCCStatus(status?: AccountStatus): boolean {
  if (!status) {
    return false
  }
  return ['BANKRUPT', 'STOLEN', 'LOST', 'FROZEN_OR_FRAUD', 'CLOSED'].includes(
    status,
  )
}

export function getDashDash(
  assistiveText: string,
  asSpan?: true,
): React.ReactElement {
  return (
    <TextBody tag={asSpan ? 'span' : 'p'} size="md">
      <span aria-hidden="true">--</span>
      <ScreenReaderOnly>{assistiveText}</ScreenReaderOnly>
    </TextBody>
  )
}

export function mortgageAccountAllytmlnText({
  type,
  nickName,
  name,
}: Account): string {
  return type === AccountType.MORTGAGE
    ? `mortgage-mortgageAccount-MORTGAGE`
    : 'mortgage-mortgageAccount-HOME-EQUITY'
}

export function getDateDifferenceFromCurrentDate(date: string): number {
  const msInDay = 24 * 60 * 60 * 1000

  return Math.round((new Date().getTime() - new Date(date).getTime()) / msInDay)
}

export function isAccountClosed(account: Account): boolean {
  return account.status === 'CLOSED'
}

export function calculateTotalBalance(
  depositAccounts: Account[],
  investAccounts: Account[],
): number {
  const depositBalance = depositAccounts.reduce((total, acc) => {
    if (acc.type === AccountType.CDA) {
      return total + (acc.balance?.current ?? 0)
    }
    return total + (acc.balance?.available ?? 0)
  }, 0)
  const investBalance = investAccounts.reduce(
    (total, acc) => total + (acc.value?.total ?? 0),
    0,
  )

  return depositBalance + investBalance
}

type Roles = keyof AllyUserRole | 'debtPlan'
export function getRoles(
  session: HostServices['session'] | null,
): AllyUserRole & { debtPlan: boolean; isOnly: (roleName: Roles) => boolean } {
  if (!session) {
    logRocket.captureMessage('ally-next-remote-dashboard: Session is null')
    throw new Error('Session is null')
  }
  const roles: { [key in Roles]: boolean } = {
    bank: !!session.data?.allyUserRole.bank,
    investment: !!session.data?.allyUserRole.investment,
    auto: !!session.data?.allyUserRole.auto,
    mortgage: !!session.data?.allyUserRole.mortgage,
    wealth: !!session.data?.allyUserRole.wealth,
    creditcard: !!session.data?.allyUserRole.creditcard,
    debtPlan: !!(
      session.data?.relationships?.tools?.debtReduction?.status === 'ACTIVE'
    ),
    guest: !!session.data?.allyUserRole.guest,
  }
  return {
    ...roles,
    isOnly: (roleName: Roles): boolean =>
      Object.entries(roles).every(([key, value]) =>
        key === roleName ? value : !value,
      ),
  }
}

export function getCreditCardName(account: Account): CreditCardNames {
  switch (account.type) {
    case AccountType.UNLIMITED:
      return 'Ally Unlimited Cash Back Credit Card'
    case AccountType.EVERYDAY:
      return 'Ally Everyday Cash Back Credit Card'
    case AccountType.PLATINUM:
      return 'Ally Platinum Credit Card'
    default:
      return 'Ally Everyday Cash Back Credit Card'
  }
}

export function hasAcceptedBankruptcy(): boolean {
  const interruptsStatus = sessionStorage.getItem('interruptsStatus')
  return interruptsStatus && JSON.parse(interruptsStatus).bankruptcyAccepted
}

export function capitalizeFirstLetter(
  str: string,
  locale: string = navigator.language,
): string {
  return str.replace(/^\p{CWU}/u, char => char.toLocaleUpperCase(locale))
}

export function mobileAccountCardAllyTM(account: Account): string {
  const hasSepIraAccount = account.meta?.retirementPlanType === 'SEP'
  const hasRothIraAccount = account.meta?.retirementPlanType === 'Roth'
  const hasTraditionalIraAccount =
    account.meta?.retirementPlanType === 'Traditional'

  switch (account.type) {
    case AccountType.DDA:
      return 'deposit-interestCheckingAccount-DDA'
    case AccountType.CDA:
      if (hasSepIraAccount) return 'deposit-cdaAccount-IRA-SEP'
      if (hasRothIraAccount) return 'deposit-cdaAccount-IRA-ROTH'
      if (hasTraditionalIraAccount) return 'deposit-cdaAccount-IRA-TRADITIONAL'
      return 'deposit-cdAccount-CDA'
    case AccountType.SDA:
      if (hasSepIraAccount) return 'deposit-sdaAccount-IRA-SEP'
      if (hasRothIraAccount) return 'deposit-sdaAccount-IRA-ROTH'
      if (hasTraditionalIraAccount) return 'deposit-sdaAccount-IRA-TRADITIONAL'
      return 'deposit-savingsAccount-SDA'
    case AccountType.MMA:
      return 'deposit-savingsAccount-MMA'
    case AccountType.CMG:
      return 'deposit-savingsAccount-CMG'
    case AccountType.INVS:
      return 'invest-selfDirectedInvestAccount-INVS'
    case AccountType.INVA:
      return 'invest-roboPortfolioInvestAccount-INVA'
    case AccountType.INVW:
      return 'wealth-wealthAccount-INVW'
    case AccountType.EVERYDAY:
      return 'creditCard-creditCardAccount-EVERYDAY-Card'
    case AccountType.PLATINUM:
      return 'creditCard-creditCardAccount-PLATINUM-Card'
    case AccountType.UNLIMITED:
      return 'creditCard-creditCardAccount-UNLIMITED-Card'
    case AccountType.LOAN:
      return 'auto-autoAccount-LOAN'
    case AccountType.OPERATING_LEASE:
      return 'auto-autoAccount-OPERATING-LEASE'
    case AccountType.MORTGAGE:
      return 'mortgage-mortgageAccount-MORTGAGE'
    case AccountType.HOME_EQUITY:
      return 'mortgage-mortgageAccount-HOME-EQUITY'
    default:
      // Should not be accessible if account data is being sent correctly
      return ''
  }
}
